import { Logger, engineComponent, StringUtils } from "neo-common";
import { HtmlController } from "../HtmlController";
import { Languages } from "../../Utils/Languages";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameApp } from "../../GameApp";
import { AppContext } from "../../../Common/AppContext";

/**
 * HtmlElement handle basic properties for all kind of html elements.
 * @class HtmlElement
 */
@engineComponent(ComponentTypes.HtmlElement)
export class HtmlElement {
    data: string;

    instance: HTMLElement;

    rid: string;
    ridType: string;
    ridFilter: string;

    private currentValue: string;

    constructor(tagName: string = "div") {
        if (tagName !== "") {
            this.instance = document.createElement(tagName);
        }
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        /*
         * CSS id reference.
         * @property id
         * @type string
         * @default null
         */
        if (node.hasAttribute("id")) {
            this.instance.id = node.getAttribute("id");
        }

        /*
         * Filter text when reach some values. For example NO_ZERO will dont show zero values.
         * @property RidFilter
         * @type string
         * @default null
         */
        if (node.hasAttribute("RidFilter")) {
            this.ridFilter = node.getAttribute("RidFilter");
        }

        /*
         * RidType is typing for formating text. For example use MONEY to have number on two decimal places.
         * @property RidType
         * @type string
         * @default null
         */
        if (node.hasAttribute("RidType")) {
            this.ridType = node.getAttribute("RidType");
        }

        /*
         * Rid is key from languages.
         * @property Rid
         * @type string
         * @default null
         */
        if (node.hasAttribute("Rid")) {
            this.rid = node.getAttribute("Rid");
            HtmlController.RidElements.push(this);
            this.setText();
        }

        /*
         * CssClass is html class attribute.
         * @property CssClass
         * @type string
         * @default null
         */
        if (node.hasAttribute("CssClass")) {
            this.instance.className = node.getAttribute("CssClass");
        }

        /*
         * Test is html data attribute for automated testing support.
         * @property Test
         * @type string
         * @default null
         */
        if (node.hasAttribute("Test")) {
            this.instance.dataset["test"] = node.getAttribute("Test");
        }

        /*
         * Data will handle dynamic texts dynamic from system.
         * @property Data
         * @type string
         * @default null
         */
        if (node.hasAttribute("Data")) {
            this.data = node.getAttribute("Data");
            HtmlController.registerDataElement(node.getAttribute("Data"), this);
        }

        if (node.hasAttribute("Lang") && this.instance != null && GameApp.language != null) {
            this.instance.setAttribute("Language", GameApp.language);
        }

        if (node.hasAttribute("ConfigCondition") && AppContext.gameConfiguration != null) {
            let condition = node.getAttribute("ConfigCondition").split("|");

            if (condition.length == 2) {
                const config = AppContext.gameConfiguration[condition[0]].toString();

                if (config != condition[1]) {
                    this.instance.parentElement.removeChild(this.instance);
                }
            }
        }
    }

    /**
     * Set text content to instance if exists
     * @method setText
     * @param value string
     * @return void
     */
    setText(value?: string): void {
        var text: string = "";

        if (value != null) {
            text = value;
            this.currentValue = value;
        }

        if (this.instance != null) {
            // find out rid from languages if element has rid
            if (this.rid != null) {
                if (Languages.getRid(this.rid) != null) {
                    if (!StringUtils.isNullOrEmpty(text)) {
                        var texts: Array<string> = text.split(";");
                        if (texts.length === 2) {
                            text = Languages.getRid(this.rid, texts[0], texts[1]);
                        } else {
                            text = Languages.getRid(this.rid, text);
                        }
                    } else {
                        text = Languages.getRid(this.rid);
                    }
                }
            }

            // format text with ridType
            if (this.ridType != null) {
                text = Languages.formatText(this.ridType, text);
            }

            // filter text based on rid filter
            if (this.ridFilter != null) {
                text = Languages.filterText(this.ridFilter, text);

                if (this.ridFilter === Languages.FilterNoZero && this.instance.classList.contains("bonus")) {
                    if (text === "") {
                        this.instance.classList.add("_hidden");
                    } else {
                        this.instance.classList.remove("_hidden");
                    }
                }
            }

            // set text to instance
            this.instance.innerHTML = text;
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }

    updateText(): void {
        var text: string = "";

        if (this.currentValue != null) {
            text = this.currentValue;
        }

        if (this.instance != null) {
            // find out rid from languages if element has rid
            if (this.rid != null) {
                if (Languages.getRid(this.rid) != null) {
                    if (!StringUtils.isNullOrEmpty(text)) {
                        var texts: Array<string> = text.split(";");
                        if (texts.length === 2) {
                            text = Languages.getRid(this.rid, texts[0], texts[1]);
                        } else {
                            text = Languages.getRid(this.rid, text);
                        }
                    } else {
                        text = Languages.getRid(this.rid);
                    }
                }
            }

            // format text with ridType
            if (this.ridType != null) {
                text = Languages.formatText(this.ridType, text);
            }

            // filter text based on rid filter
            if (this.ridFilter != null) {
                text = Languages.filterText(this.ridFilter, text);

                if (this.ridFilter === Languages.FilterNoZero && this.instance.classList.contains("bonus")) {
                    if (text === "") {
                        this.instance.classList.add("_hidden");
                    } else {
                        this.instance.classList.remove("_hidden");
                    }
                }
            }

            // set text to instance
            this.instance.innerHTML = text;
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }

    getText(): string {
        if (this.currentValue != null) {
            return this.currentValue;
        }

        return null;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected logError(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/HTMLComponents/Elements/HtmlElement.ts
 **/